/**
 * Landing v3 — getting the app (user 2026-09-28): the store this device
 * installs from, the floating store button, and the store glyphs every
 * get-the-app control shares (the floating button, the footer).
 *
 * On a phone or tablet the store is the page's ONE primary action — a primary
 * button floating at the bottom of the screen (Figma 116:885) — and signing
 * up on the web steps back to secondary. A computer installs
 * neither app, so it gets no floating button: it keeps Get Started and the
 * top bar's QR codes, the bridge from a desk to a phone.
 */
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
} from '@flashka/shared/ui';

import { motion, useReducedMotion } from 'framer-motion';
import { SmartphoneIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppStore, StoreDevice } from '@/lib/store-links';
import { cn } from '@/lib/utils';

import { GetStartedCta } from './landing-chrome';
import { WandIcon } from './landing-icons';
import { EASE_OUT, KA_GLIDE } from './landing-ka';
import { useOpenStore } from './landing-store';

/** The DS Button's icon contract (see `Button.types.ts`). */
type IconProps = { size?: number; color?: string; className?: string };

/** The App Store icon — brand artwork, so a button's tint is ignored. */
export function AppStoreGlyph({ size = 20, className }: IconProps) {
  return (
    <img
      src="/assets/landing/app-store.png"
      alt=""
      style={{ width: size, height: size }}
      className={cn('rounded-[4px]', className)}
    />
  );
}

/** Google Play's triangle, without its app icon's white disc (as in the
 *  get-the-app menu), so neither theme shows a white patch. */
export function PlayStoreGlyph({ size = 20, className }: IconProps) {
  return (
    <img
      src="/assets/landing/play-store-glyph.png"
      alt=""
      style={{ width: size, height: size }}
      className={cn('object-contain', className)}
    />
  );
}

export const STORE_GLYPH: Record<AppStore, (p: IconProps) => React.ReactNode> = {
  ios: AppStoreGlyph,
  android: PlayStoreGlyph,
};

export const STORE_LABEL_KEY: Record<AppStore, string> = {
  ios: 'landingV3.nav.appStore',
  android: 'landingV3.nav.playStore',
};

/** How far the floating button moves left while Ka rests beside it (Figma
 *  116:885): half of Ka's 64px card and the 8px between them. */
const KA_SIDE_SHIFT = (64 + 8) / 2;

/**
 * The floating call to action's slot, bottom-centre (Figma 116:885): a primary
 * button, 278px wide (narrower where the screen is), 24px off the bottom. On
 * the landing it sits beside Ka while he rests in the corner and centres when
 * he is away. It stays on screen through every scroll, the footer included
 * (user 2026-10-02: the page's one call to action is never out of reach; the
 * footer leaves room under its last links for it); only the page can step it
 * aside (`hidden`).
 */
function FloatingSlot({
  kaBeside,
  hidden,
  children,
}: {
  kaBeside: boolean;
  hidden: boolean;
  children: ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      initial={false}
      animate={hidden ? { opacity: 0, y: 24 } : { opacity: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.3, ease: EASE_OUT }}
      className="pointer-events-none fixed inset-x-0 z-30 flex justify-center"
      style={{ bottom: 'max(24px, env(safe-area-inset-bottom))' }}>
      <motion.div
        inert={hidden}
        initial={false}
        animate={{ x: kaBeside ? -KA_SIDE_SHIFT : 0 }}
        transition={reduceMotion ? { duration: 0 } : KA_GLIDE}
        className={cn(
          'w-[min(278px,calc(100vw-112px))]',
          hidden ? 'pointer-events-none' : 'pointer-events-auto',
        )}>
        {children}
      </motion.div>
    </motion.div>
  );
}

/**
 * The floating store button: one tap opens this device's store; a device we
 * cannot place gets a menu of both.
 */
export function FloatingGetApp({
  store,
  kaBeside = false,
  hidden = false,
}: {
  store: Exclude<StoreDevice, null>;
  /** Ka rests in the corner beside it (the landing's phone layout). */
  kaBeside?: boolean;
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const openStore = useOpenStore();
  // "Free" on the store button (team 2026-10-02): the store glyph already
  // says it is the app, the word says it costs nothing.
  const label = t('landingV3.nav.getAppFree');

  return (
    <FloatingSlot kaBeside={kaBeside} hidden={hidden}>
      {store === 'both' ? (
        <Dropdown size="l">
          <DropdownTrigger>
            {({ onPress }) => (
              <Button
                variant="primary"
                size="xxl"
                fullWidth
                leftIcon={SmartphoneIcon}
                onPress={onPress}>
                {label}
              </Button>
            )}
          </DropdownTrigger>
          <DropdownContent side="top" align="center" sideOffset={12} minWidth={220}>
            {(['ios', 'android'] as const).map((s) => {
              const Glyph = STORE_GLYPH[s];
              return (
                <DropdownItem key={s} onSelect={() => openStore(s, 'floating', true)}>
                  <span className="flex items-center gap-3">
                    <Glyph size={22} />
                    {t(STORE_LABEL_KEY[s])}
                  </span>
                </DropdownItem>
              );
            })}
          </DropdownContent>
        </Dropdown>
      ) : (
        <Button
          variant="primary"
          size="xxl"
          fullWidth
          leftIcon={STORE_GLYPH[store]}
          accessibilityLabel={`${label} · ${t(STORE_LABEL_KEY[store])}`}
          onPress={() => openStore(store, 'floating', true)}>
          {label}
        </Button>
      )}
    </FloatingSlot>
  );
}

/**
 * The same slot on a computer, which installs neither app (the phone layout
 * in a narrow window): the page's sign-up, in the frame's place.
 */
export function FloatingSignUp({
  kaBeside = false,
  hidden = false,
}: {
  kaBeside?: boolean;
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <FloatingSlot kaBeside={kaBeside} hidden={hidden}>
      <GetStartedCta
        placement="floating"
        size="xxl"
        variant="primary"
        fullWidth
        leftIcon={WandIcon}>
        {t('landingV3.cta')}
      </GetStartedCta>
    </FloatingSlot>
  );
}
